import {getVersion} from '@aboutyou/config';
import {sha256} from '@aboutyou/legacy-tracking-sdk/src/ay-tracking/global-utils/helper-implementations/sha256';
import {dispatchCheckoutPaymentLoadTimingEvent} from '../CheckoutWebComponent/ReportPaymentLoadTiming';
import {Z_BUY_NOW_PAY_LATER_PAYMENT_KEY} from '../components/PaymentSlots/constants';
import type {CheckoutEntryDetails} from '../hooks/useCheckoutEntryDetails';
import {
  ScaylePaymentOption,
  ScayleShippingOption,
  ScayleState,
} from '../scayle-state/useScayleState';
import {resetActivatedExperiments} from './ExperimentActivationTriggerSlot';

export type CheckoutTrackingEvent =
  | {
      event: 'shop_init';
      shop_currency?: string;
      shop_id: number;
      locale?: string;
    }
  | {
      event: 'customer_data';
      age?: number;
      customer_id: number;
      eh: string;
      customer_type: string;
      logged_in: string;
    }
  | {
      event: 'content_view';
      page_type: string;
    }
  | {
      event: 'experiment_activation';
      experiment_name: string;
      experiment_variant: string;
      event_timestamp_ay: number;
    }
  | {
      event: 'error';
      label: string;
      action?: string;
      statusCode?: string;
    }
  | {
      event: 'feature';
      label: 'payment_methods_loading_time';
      action: 'timing';
      name: null;
      value: number;
      includes_shipping?: boolean;
    }
  | {
      event: 'feature';
      label: 'payment_methods_visible';
      action: 'impression';
      name: null;
    }
  | {
      event: 'feature';
      action: 'impression';
      name: typeof Z_BUY_NOW_PAY_LATER_PAYMENT_KEY;
      label: string;
    }
  | {
      event: 'feature';
      action: 'submit';
      name: 'add_shipping_info';
      label: string;
      shipping_type: string;
      shipping_method: string;
    }
  | {
      event: 'feature';
      action: 'click';
      name: 'payment_method_preselect';
      label: string;
    }
  | {
      event: 'feature';
      action: 'click';
      name: 'payment_method';
      label: string;
      value: number;
      location_id: string;
    }
  | {
      event: 'feature';
      action: 'click';
      name: 'buy_now';
      label: string;
    }
  | {
      event: 'add_payment_info';
      ecommerce: object;
      payment_type: string;
      label: null;
    }
  | {
      event: 'feature';
      action: 'click';
      name: 'continue_to_payment';
      label: string;
    }
  | {
      event: 'complete_checkout';
    }
  | {
      event: 'add_to_cart';
    }
  | {
      event: 'remove_from_cart';
    }
  | {
      event: 'bnpl_status';
      context: {
        status: boolean;
        risk_check_1_result: boolean;
        hyped_article: boolean;
        age: boolean;
        min_ov: boolean;
        max_ov: boolean;
      };
    };

// eslint-disable-next-line complexity
export const mapCheckoutTrackingEvent = ({
  checkoutDataLayerEvent,
  device,
  search,
  gaSessionId,
  scayleState,
  bnplOption,
  checkoutEntryDetails,
  selectedPaymentOption,
  selectedShippingOption,
}: {
  checkoutDataLayerEvent: CheckoutTrackingEvent;
  device: string;
  search: URLSearchParams;
  gaSessionId?: string;
  scayleState?: ScayleState | null;
  checkoutEntryDetails: CheckoutEntryDetails | null;
  selectedPaymentOption: ScaylePaymentOption | null;
  selectedShippingOption: ScayleShippingOption | null;
  bnplOption: ScaylePaymentOption | null;
}) => {
  const isExpressCheckout = scayleState?.isExpressCheckout ?? false;
  const isApp = device === 'app';

  if (checkoutDataLayerEvent.event === 'shop_init') {
    const originalEvent = {
      ...checkoutDataLayerEvent,
      device,
    };
    // Original event which will be cleaned up in a follow-up
    const checkoutInitEvent = createCoInitEvent({
      device,
      transactionId: scayleState?.orderState.id,
      basketId: scayleState?.orderState.basket?.id,
      shopId: String(checkoutDataLayerEvent.shop_id),
      shopCurrency: checkoutDataLayerEvent.shop_currency,
      locale: checkoutDataLayerEvent.locale,
    });

    /**
     * Reset activate experiments on checkout init.
     * This is necessary because user may navigate back to Basket and click on "Go to checkout" again,
     * which should be considered as a new checkout session and thus trigger experiment activations again.
     */
    resetActivatedExperiments();

    return [originalEvent, checkoutInitEvent];
  }

  if (checkoutDataLayerEvent.event === 'customer_data') {
    const customerDataEvent = createCustomerDataEvent({
      device,
      customerId: checkoutDataLayerEvent.customer_id,
      eh: checkoutDataLayerEvent.eh,
      customerType: checkoutDataLayerEvent.customer_type,
      loggedIn: checkoutDataLayerEvent.logged_in,
      gender: scayleState?.customer?.data?.gender,
      age: checkoutDataLayerEvent.age,
    });

    const clientSignalsEvent = createClientSignalsEvent({
      device,
      search,
      gaSessionId,
      customerId: checkoutDataLayerEvent.customer_id,
      checkoutEntryDetails,
    });

    /**
     * We want the customer_data event only for the app, as the web component emits its own customer_data event after shop_init
     */
    if (isApp) {
      return [customerDataEvent, clientSignalsEvent];
    }

    return clientSignalsEvent;
  }

  if (checkoutDataLayerEvent.event === 'content_view') {
    const contentViewEvent = {
      event: 'content_view',
      device,
      page_type: isExpressCheckout
        ? 'checkout:klarna_express'
        : checkoutDataLayerEvent.page_type,
    };
    return contentViewEvent;
  }

  if (checkoutDataLayerEvent.event === 'experiment_activation') {
    const experimentActivationEvent = {
      ...checkoutDataLayerEvent,
      device,
    };
    return experimentActivationEvent;
  }

  if (checkoutDataLayerEvent.event === 'error') {
    const errorEvent = {
      event: 'error',
      device,
      error: checkoutDataLayerEvent.label,
      label: checkoutDataLayerEvent.action ?? null,
      status_code: checkoutDataLayerEvent.statusCode,
    };
    return errorEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.action === 'timing' &&
    checkoutDataLayerEvent.label === 'payment_methods_loading_time'
  ) {
    const paymentMethodsLoadEvent = {
      event: 'system',
      device,
      name: 'payment_methods',
      action: 'load',
      event_scope: 'long',
      context: {
        bnpl_disabled: bnplOption ? bnplOption.isDisabled : null,
        payment_type: selectedPaymentOption?.paymentOptionKey ?? null,
        load_time_millis: checkoutDataLayerEvent.value,
        includes_shipping: checkoutDataLayerEvent.includes_shipping ?? false,
      },
    };

    // Dispatching a custom event to report payment load timing, check ReportPaymentLoadTiming.tsx for details
    dispatchCheckoutPaymentLoadTimingEvent({
      type: 'PaymentLoadTiming',
      value: checkoutDataLayerEvent.value,
      includes_shipping: checkoutDataLayerEvent.includes_shipping ?? false,
    });

    return paymentMethodsLoadEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.action === 'impression' &&
    checkoutDataLayerEvent.name === Z_BUY_NOW_PAY_LATER_PAYMENT_KEY &&
    checkoutDataLayerEvent.label === ZPS_RISK_CHECK_2_DENIED_LABEL
  ) {
    return createZpsRiskCheck2Event(device, 'fail');
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.label === 'payment_methods_visible'
  ) {
    const paymentMethodsImpressionEvent = {
      event: 'component',
      device,
      name: 'payment_methods',
      action: 'impression',
      content_type: 'radio_button',
      event_scope: 'long',
      page_type: isExpressCheckout
        ? 'checkout:klarna_express'
        : 'checkout:payment',
      context: {
        bnpl_disabled: bnplOption ? bnplOption.isDisabled : null,
        payment_type: selectedPaymentOption?.paymentOptionKey ?? null,
      },
    };
    return paymentMethodsImpressionEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.name === 'add_shipping_info'
  ) {
    const originalEvent = {
      ...checkoutDataLayerEvent,
      device,
    };
    // Original event which will be cleaned up in a follow-up
    return originalEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.name === 'continue_to_payment'
  ) {
    const continueToPayment = {
      event: 'component',
      device,
      name: 'continue_to_payment',
      content_type: 'button',
      action: 'submit',
      event_scope: 'long',
      page_type: 'checkout:shipping',
      context: {
        delivery_type: selectedShippingOption?.deliveryOptionKey ?? null,
      },
    };

    return continueToPayment;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.name === 'payment_method_preselect'
  ) {
    // Original event which will be cleaned up in a follow-up
    const originalEvent = {
      ...checkoutDataLayerEvent,
      device,
    };
    return originalEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.action === 'click' &&
    checkoutDataLayerEvent.name === 'buy_now'
  ) {
    const buyNowButtonClickEvent = {
      device,
      event: 'component',
      name: 'buy_now',
      content_type: 'button',
      action: 'submit',
      event_scope: 'long',
      page_type: isExpressCheckout
        ? 'checkout:klarna_express'
        : 'checkout:payment',

      context: {
        payment_type: selectedPaymentOption?.paymentOptionKey ?? null,
        delivery_type: selectedShippingOption?.deliveryOptionKey ?? null,
        carriers: selectedShippingOption?.carrierKey ?? null,
      },
    };

    return buyNowButtonClickEvent;
  }

  if (
    checkoutDataLayerEvent.event === 'feature' &&
    checkoutDataLayerEvent.action === 'click' &&
    checkoutDataLayerEvent.name === 'payment_method'
  ) {
    // Original event which will be cleaned up in a follow-up
    const originalEvent = {
      ...checkoutDataLayerEvent,
      device,
    };

    const selectPaymentMethodEvent = {
      event: 'component',
      device,
      name: 'payment_methods',
      content_type: 'radio_button',
      action: 'click',
      event_scope: 'long',
      location_id: checkoutDataLayerEvent.location_id,
      page_type: 'checkout:payment',
      context: {
        payment_type: checkoutDataLayerEvent.label.split('|').reverse()[0],
        index: checkoutDataLayerEvent.value,
      },
    };

    return [originalEvent, selectPaymentMethodEvent];
  }

  if (checkoutDataLayerEvent.event === 'add_payment_info') {
    // Original event which will be cleaned up in a follow-up
    const originalEvent = {
      ...checkoutDataLayerEvent,
      device,
    };
    return originalEvent;
  }

  if (checkoutDataLayerEvent.event === 'complete_checkout') {
    const completeCheckoutEvent = {
      event: 'complete_checkout',
      device,
      payment_type: selectedPaymentOption?.paymentOptionKey ?? null,
      page_type: isExpressCheckout
        ? 'checkout:klarna_express'
        : 'checkout:payment',
      delivery_type: selectedShippingOption?.deliveryOptionKey ?? null,
      carriers: selectedShippingOption?.carrierKey ?? null,
    };

    if (selectedPaymentOption?.paymentOptionKey === 'spay_zps_bnpl') {
      return [createZpsRiskCheck2Event(device, 'pass'), completeCheckoutEvent];
    }

    return completeCheckoutEvent;
  }

  if (
    ['add_to_cart', 'remove_from_cart'].includes(checkoutDataLayerEvent.event)
  ) {
    return {
      ...checkoutDataLayerEvent,
      device,
    };
  }

  if (checkoutDataLayerEvent.event === 'bnpl_status') {
    return {
      event: 'system',
      device,
      name: 'bnpl_status',
      action: 'load',
      event_scope: 'long',
      context: checkoutDataLayerEvent.context,
    };
  }

  return null;
};

const getDevicePlatform = (device: string, search: URLSearchParams) => {
  if (device !== 'app') return 'web';
  return search.get('device') === 'android' ? 'Android' : 'iOS';
};

/**
 * Builds synthetic `co_init`, `customer_data`, and `client_signals` events from
 * `checkoutEntryDetails` when the Scayle web component fails before dispatching
 * its own `shop_init` / `customer_data` events. Fields not derivable from the
 * JWT or app config (e.g. `shop_currency`, `eh`, `gender`) are emitted as null.
 */
export const buildFallbackInitEvents = ({
  device,
  search,
  gaSessionId,
  checkoutEntryDetails,
}: {
  device: string;
  search: URLSearchParams;
  gaSessionId?: string;
  checkoutEntryDetails: CheckoutEntryDetails | null;
}) => {
  const jwtPayload = checkoutEntryDetails?.jwtPayload;
  const customerId = jwtPayload?.customData?.customerId ?? null;

  const isApp = device === 'app';

  const coInitEvent = createCoInitEvent({
    device,
    basketId: jwtPayload?.basketId,
    shopId: jwtPayload?.shopId != null ? String(jwtPayload.shopId) : undefined,
  });

  const customerDataEvent = createCustomerDataEvent({
    device,
    customerId,
    eh: null,
    customerType: null,
    loggedIn: null,
    gender: null,
    age: null,
  });

  const clientSignalsEvent = createClientSignalsEvent({
    device,
    search,
    gaSessionId,
    customerId,
    checkoutEntryDetails,
  });

  // For app, we emit all three events as the web component doesn't emit any. For web, we only emit `client_signals` as the web component emits its own `shop_init` and `customer_data` events which would lead to duplicates if we emitted them here as well.
  if (isApp) {
    return [coInitEvent, customerDataEvent, clientSignalsEvent];
  }
  return [coInitEvent, clientSignalsEvent];
};

const createCoInitEvent = ({
  device,
  transactionId,
  basketId,
  shopId,
  shopCurrency,
  locale,
}: {
  device: string;
  transactionId?: string;
  basketId?: string;
  shopId?: string;
  shopCurrency?: string;
  locale?: string;
}) => ({
  event: 'co_init',
  device,
  transaction_id: transactionId,
  basket_id: basketId,
  shop_id: shopId,
  shop_currency: shopCurrency,
  locale,
  shop_version: `${getVersion()}`,
  sdk_version: '14.68.0',
  co_version: window.scayle?.config?.version, // there could be a case where CO is not loaded at all, making window.scayle.config undefined, so we make this field optional
});

const createCustomerDataEvent = ({
  device,
  customerId,
  eh,
  customerType,
  loggedIn,
  gender,
  age,
}: {
  device: string;
  customerId: number | null;
  eh: string | null;
  customerType: string | null;
  loggedIn: string | null;
  gender: string | null | undefined;
  age: number | null | undefined;
}) => ({
  event: 'customer_data',
  device,
  customer_id: customerId,
  eh,
  customer_type: customerType,
  logged_in: loggedIn,
  gender,
  age,
  customer_id_hash: customerId != null ? sha256(String(customerId)) : null,
});

const createClientSignalsEvent = ({
  device,
  search,
  gaSessionId,
  customerId,
  checkoutEntryDetails,
}: {
  device: string;
  search: URLSearchParams;
  gaSessionId?: string;
  customerId: number | null;
  checkoutEntryDetails: CheckoutEntryDetails | null;
}) => ({
  event: 'client_signals',
  device,
  user_agent: navigator.userAgent,
  device_type: device,
  device_platform: getDevicePlatform(device, search),
  ga_session_id: gaSessionId,
  app: checkoutEntryDetails?.appConfig?.app ?? null,
  consent: checkoutEntryDetails?.appConfig?.consent ?? null,
  ab_test: {
    user_id: customerId,
    tests:
      checkoutEntryDetails?.jwtPayload?.experiments
        ?.map(exp => `${exp.experimentKey}|${exp.variantKey}`)
        .join(',') ?? null,
  },
  attribution:
    device === 'app'
      ? {deep_link_url: window.location.href}
      : {search_params: window.location.search},
});

const ZPS_RISK_CHECK_2_DENIED_LABEL = `error|${Z_BUY_NOW_PAY_LATER_PAYMENT_KEY}|isDisabled|riskCheckDenied`;

const createZpsRiskCheck2Event = (device: string, result: 'pass' | 'fail') => ({
  event: 'system',
  device,
  name: 'zps_risk_check_2',
  action: 'load',
  event_scope: 'long',
  context: {result},
});
